import { match, P } from 'ts-pattern';
import type { ApiSourceBundle } from '@interxfi/api-client/registry/api-source';
import { dereferencePackage, dereferenceSchema } from '@interxfi/j5-parse/dereference';
import type { DereferencedParsedSchema, ParsedPackage, ParsedSchema, ParsedSchemaWithRef } from '@interxfi/j5-parse/types/parsed';
import { exampleSchema } from '../example-gen';
import { buildApiDocsVersionKey, useDocsStore } from '../state';
import { type ApiReferencePackageStructureItem, getApiReferenceNavigationStructureForPackage } from './layout/api-reference-structure';

export interface ApiReferencePackageStructure {
  dereferencedPackage: ParsedPackage<DereferencedParsedSchema>;
  structure: ApiReferencePackageStructureItem[];
}

function getFullGrpcName(schema: ParsedSchemaWithRef | undefined): string | undefined {
  return match(schema)
    .with({ object: P.not(P.nullish) }, (s) => s.object.fullGrpcName)
    .with({ oneOf: P.not(P.nullish) }, (s) => s.oneOf.fullGrpcName)
    .with({ enum: P.not(P.nullish) }, (s) => s.enum.fullGrpcName)
    .with({ polymorph: P.not(P.nullish) }, (s) => s.polymorph.fullGrpcName)
    .with({ $ref: P.not(P.nullish) }, (s) => s.$ref.replace('#/schemas/', ''))
    .otherwise(() => undefined);
}

function digForRefs(schema: ParsedSchemaWithRef, schemas: Map<string, ParsedSchema>, visited: Set<string> = new Set()) {
  const fullGrpcName = getFullGrpcName(schema);

  const enqueue = (child: ParsedSchemaWithRef | undefined) => {
    if (child) {
      digForRefs(child, schemas, visited);
    }
  };

  // If this schema is a $ref, resolve and dive deeper
  if ('$ref' in schema && fullGrpcName) {
    const referencedSchema = schemas.get(fullGrpcName);
    if (referencedSchema) {
      enqueue(referencedSchema);
    }

    return;
  }

  if (fullGrpcName) {
    if (visited.has(fullGrpcName)) {
      return;
    }

    visited.add(fullGrpcName);
  }

  if ('object' in schema && schema.object?.properties) {
    for (const [, prop] of schema.object.properties) {
      enqueue(prop.schema);
    }

    return;
  }

  if ('oneOf' in schema && schema.oneOf?.properties) {
    for (const [, prop] of schema.oneOf.properties) {
      enqueue(prop.schema);
    }

    return;
  }

  if ('polymorph' in schema && schema.polymorph?.properties) {
    for (const [, member] of schema.polymorph.properties) {
      for (const [, prop] of member) {
        enqueue(prop.schema);
      }
    }
  }

  if ('array' in schema && schema.array?.itemSchema) {
    enqueue(schema.array.itemSchema);
  }

  if ('map' in schema) {
    enqueue(schema.map.itemSchema);
    enqueue(schema.map.keySchema);
  }
}

function collectPackageSchemas(pkg: ParsedPackage, schemas: Map<string, ParsedSchema>): Set<string> {
  const allRefs = new Set<string>();

  const enqueueSchema = (schema: ParsedSchemaWithRef | undefined) => {
    if (schema) {
      digForRefs(schema, schemas, allRefs);
    }
  };

  for (const service of pkg.services || []) {
    for (const method of service.methods) {
      enqueueSchema(method.requestBody);
      enqueueSchema(method.responseBody);

      if (method.pathParameters) {
        for (const [, param] of method.pathParameters) {
          enqueueSchema(param.schema);
        }
      }

      if (method.queryParameters) {
        for (const [, param] of method.queryParameters) {
          enqueueSchema(param.schema);
        }
      }
    }
  }

  return allRefs;
}

// For stable refs
const mappedPackageCache = new Map<string, ApiReferencePackageStructure>();

export function mapPackageData(
  bundle: ApiSourceBundle,
  registryVersion: string | undefined,
  version: string,
  builtAt: string,
  pkg: ParsedPackage,
  schemas: Map<string, ParsedSchema>,
): ApiReferencePackageStructure {
  const { mappedSchemasByVersion, addMappedSchema } = useDocsStore.getState();
  const versionKey = buildApiDocsVersionKey(bundle, registryVersion, version);
  const versionCacheKey = `${versionKey}-${pkg.name}`;

  if (mappedPackageCache.has(versionCacheKey)) {
    return mappedPackageCache.get(versionCacheKey)!;
  }

  const allRefs = collectPackageSchemas(pkg, schemas);
  const mappedSchemas = new Map<string, DereferencedParsedSchema>();
  const generatedExamples = new Map<string, any>();

  const cachedDataForVersion = mappedSchemasByVersion[versionKey];
  const toProcess = new Set(allRefs);

  const visitedRefs = new Set<string>();
  for (const ref of toProcess) {
    const cachedSchema = cachedDataForVersion?.[ref];
    const schema = cachedSchema ?? schemas.get(ref);
    if (schema && !cachedSchema) {
      exampleSchema(schema, schemas, generatedExamples, visitedRefs);
    }
  }

  // Deref and cache
  for (const ref of toProcess) {
    const cachedSchema = cachedDataForVersion?.[ref];
    const schema = cachedSchema ?? schemas.get(ref);

    if (schema) {
      if (!cachedSchema) {
        addMappedSchema(bundle, registryVersion, version, builtAt, ref, schema);
      }

      const deref = dereferenceSchema(schema, schemas, mappedSchemas);
      if (deref) {
        mappedSchemas.set(ref, deref);
      }
    }
  }

  // Generate examples for package's direct schemas (request/response)
  for (const service of pkg.services) {
    for (const method of service.methods) {
      if (method.requestBody) {
        exampleSchema(method.requestBody, schemas, generatedExamples, visitedRefs);
      }

      if (method.responseBody) {
        exampleSchema(method.responseBody, schemas, generatedExamples, visitedRefs);
      }

      if (method.pathParameters) {
        for (const [name, param] of method.pathParameters) {
          exampleSchema(param.schema, schemas, generatedExamples, visitedRefs, name);
        }
      }

      if (method.queryParameters) {
        for (const [name, param] of method.queryParameters) {
          exampleSchema(param.schema, schemas, generatedExamples, visitedRefs, name);
        }
      }
    }
  }

  const dereferencedPackage = dereferencePackage(pkg, mappedSchemas);

  const packageStructure = {
    dereferencedPackage,
    structure: getApiReferenceNavigationStructureForPackage(pkg),
  } as const satisfies ApiReferencePackageStructure;

  mappedPackageCache.set(versionCacheKey, packageStructure);

  return packageStructure;
}
